import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cssClasses from './transaction.css'
import * as actions from '../../Actions/matchables';
import Spinner from '../UI/Spinner/spinner';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: 'date', numeric: false, disablePadding: true, label: 'Date' },
  { id: 'type', numeric: false, disablePadding: false, label: 'Type' },
  { id: 'reference', numeric: false, disablePadding: false, label: 'Ref' },
  { id: 'contact', numeric: false, disablePadding: false, label: 'Contact' },
  { id: 'amount', numeric: true, disablePadding: false, label: 'Amount' },
  { id: 'balanceRemaining', numeric: true, disablePadding: false, label: 'Open Balance' },
  { id: 'payment', numeric: true, disablePadding: false, label: 'Payment' },
];

function EnhancedTableHead(props) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" className={classes.tableCell}>
          {/* <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            color="default"
            inputProps={{ 'aria-label': 'select all desserts' }}
          /> */}
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
            className={classes.tableCell}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  // onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    width: '100%',
    '& td': {
      fontFamily: 'inherit',
    }
  },
  tableCell: {
    background: 'whitesmoke'
  },
  tableRow: {
    '&$selected, &$selected:hover': {
      backgroundColor: 'rgba(192,192,192,0.3)'
    }
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.3
  },
  selected: {},

  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

// Keyed by the stable codes BankDataAPI sets on QueueState (RWR-19864) rather than
// raw error text, so nothing internal reaches the screen. An absent code means the
// most recent run of that sync succeeded; an unrecognised one falls back to the
// data-error copy rather than rendering the code itself.
//
// Reloading only refetches this list, it cannot re-run the sync, which retries on its
// next scheduled run - so the copy stays inside what the button can actually do.
//
// Treat these strings as stable. Support triages RAH matching cases off whatever the
// customer reads off their screen (in ES-3475 the old "Could not fetch invoices"
// toast was quoted verbatim as the identifying symptom), so rewording them changes
// the vocabulary of incoming cases. Nothing in Confluence or Jira keys off them, so
// there is no runbook to update - which is exactly why a casual reword would go
// unnoticed until a case arrives describing a symptom nobody recognises.
const SYNC_ERROR_COPY = {
  'rah-error': 'Your Reckon Accounts book rejected the last sync, so this list may be out of date. The book may be open in single user mode, or in use by another user.',
  'data-error': 'The last sync could not be saved, so this list may be out of date. It will be retried on the next sync — contact support if it keeps happening.'
};

const FindMatchTable = (props) => {
  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('type');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = React.useState([]);
  const [staleSync, setStaleSync] = React.useState(false);
  const { searchBy, transactionType, dateRange, contacts, selected, setSelected, firstSelected, setFirstSelected, hasZeroPayment, isUndepositedSelected, isInvoiceOrBillSelected, isR1, matchables, lastMatchableSync, queueState } = props;
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);
  const listErrors = Object.keys(matchables.errors || {}).map(list => matchables.errors[list]);
  const requestedLists = Object.keys(matchables.loading || {});
  // The tick before the first request counts as loading, so the table does not flash
  // "No match found" before anything has been asked for.
  const loading = requestedLists.length === 0 || requestedLists.some(list => matchables.loading[list]);
  const colSpan = headCells.length + 1; // number of columns including checkbox column.

  // Money in reads invoices and undeposited items, money out reads bills. The two
  // money-in syncs fail independently and the backend classifies them independently, so
  // both codes are read rather than collapsed with ||: that discarded whichever came
  // second, and when invoices failed to save while undeposited items were rejected by
  // the book it hid the one message the customer can act on ("open in single user mode")
  // behind one that only says to contact support. Two different codes render both
  // sentences; the same code on both lists renders once.
  const syncErrorCodes = (isR1 || !queueState) ? []
    : props.amount > 0 ? [queueState.lastInvoiceSyncError, queueState.lastUndepositedSyncError]
    : [queueState.lastBillSyncError];
  const syncErrorMessages = syncErrorCodes
    .filter(code => code)
    .map(code => SYNC_ERROR_COPY[code] || SYNC_ERROR_COPY['data-error'])
    .filter((message, i, all) => all.indexOf(message) === i);
  const rowOptions = [10];
  // if (rows.length > 5) rowOptions.push(10);
  // if (rows.length > 10) rowOptions.push(25);

  const transactionAmount = Math.abs(props.amount);
  const invoiceSubtotal = subtotal(selected);
  const invoiceTotal = Number((transactionAmount - invoiceSubtotal).toFixed(2));

  React.useEffect(() => {
    const { bills, undeposited, invoices } = matchables;
    let matchableList = [ ...bills, ...undeposited, ...invoices ];
    matchableList.forEach((matchable, i) => {
      const rowContact = contacts.find(item => item.value === matchable.contactId)
      matchableList[i].contact = rowContact ? rowContact.label : (matchable.contactName || '');
    })

    // Rows stay interactive while a reload is in flight, so a row can be picked and then
    // disappear when the response lands (already matched, or no longer returned by the
    // sync). Drop those picks, otherwise the Match button submits a txnId that is no
    // longer matchable. Reconciled against the unfiltered list on purpose: filtering by
    // search, type or date hides rows without invalidating a selection.
    //
    // Only once every list has settled, though. Mid-flight the store is legitimately
    // empty - CLEAR_MATCH wipes it before an R1 date-range refetch, and it starts empty
    // on mount - and reconciling against that reads as "every pick has gone", throwing
    // away a part-built match the response is about to restore. The R1 refetch is also
    // the one case where narrowing the date range can drop a pick: R1 filters server
    // side, so a row outside the new range is genuinely no longer in the list.
    if (!loading && selected.length) {
      const available = {};
      matchableList.forEach(item => { available[item.txnId] = item; });
      // The first pick anchors which type and contact the rest are allowed to be, so if
      // it has gone the remaining picks are no longer a known-valid combination.
      if (firstSelected.txnId && !available[firstSelected.txnId]) {
        setSelected([]);
        setFirstSelected({});
      } else {
        let reconciled = false;
        const kept = selected.reduce((list, item) => {
          const row = available[item.txnId];
          if (!row) {
            reconciled = true;
            return list;
          }
          // A pick can also come back part paid rather than gone, so a payment typed
          // against the old balance is brought back within the new one. The total then
          // no longer covers the transaction, which disables Match and shows up in the
          // Difference line rather than being posted for more than is owed.
          if (Number(item.payment) > row.balanceRemaining) {
            reconciled = true;
            list.push({ ...item, balanceRemaining: row.balanceRemaining, payment: row.balanceRemaining });
          } else {
            list.push(item);
          }
          return list;
        }, []);
        if (reconciled) setSelected(kept);
      }
    }

    if ((searchBy && searchBy.length > 1) || transactionType || dateRange) {
      if((searchBy && searchBy.length > 1)) {
        const search = searchBy.toLowerCase();
        matchableList = matchableList.filter(item => (
          (item.reference && item.reference.toString().toLowerCase().includes(search)) 
          || item.contact.toLowerCase().includes(search)
          || Math.abs(item.amount).toFixed(2).includes(search)) || Math.abs(item.balanceRemaining).toFixed(2).includes(search));
      }
      if (transactionType) {
        matchableList = matchableList.filter(item => item.type === transactionType);
      }
      if (dateRange) {
        matchableList = matchableList.filter(item => moment(item.date).isBetween(dateRange.transactionsFrom, dateRange.transactionsTo, null, '[]'));
      }
    }
    setRows(matchableList);
    // A background reload must not move the user. This runs on every store tick, and a
    // sync-triggered reload now ticks it several times (a start and a settle per list),
    // so resetting here threw someone reading page 3 back to page 1 repeatedly with no
    // action of their own. The page is only pulled back far enough to land on rows that
    // still exist; a filter change resets it to the first page below.
    setPage(current => Math.min(current, Math.max(0, Math.ceil(matchableList.length / rowsPerPage) - 1)));
    // Still keyed on the whole store rather than the three lists: a list that settles
    // with an error leaves its rows untouched, and reconciliation has to run on that
    // tick too - it may be the one that settles the last request.
  }, [matchables, searchBy, transactionType, dateRange])

  // A new filter shows a different set of rows, so start reading them from the first page.
  React.useEffect(() => {
    setPage(0);
  }, [searchBy, transactionType, dateRange])

  const loadMatchables = () => {
    if (isR1) {
      props.amount > 0 ? props.loadR1Data(dateRange) : props.getR1Bills(dateRange);
    } else {
      props.amount > 0 ? props.loadData() : props.getBills();
    }
  };

  // The picks are kept. Reloading only refetches the list, and the reconciliation above
  // already drops whatever came back gone and pulls a part-paid pick back within its new
  // balance, so clearing protects the user from nothing. The sync-error banner sits on
  // screen for as long as that sync keeps failing - the whole time someone is building a
  // match - so a button that silently discarded their picks and typed payments was the
  // greater risk.
  const refresh = () => {
    setStaleSync(false);
    loadMatchables();
  };

  React.useEffect(() => {
    loadMatchables();
    return () => props.clearMatchables();
  }, [])

  // Re-fetch R1 data when date range changes
  const isInitialMount = React.useRef(true);
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isR1) {
      props.clearMatchables();
      loadMatchables();
    }
  }, [dateRange])

  // RAH syncs matchable items in the background (rahQueue polls the queue every few
  // seconds and reports when they last changed). Nothing used to refetch this modal's
  // lists when that happened, so an open modal kept showing pre-sync data. Reload when
  // nothing is selected; otherwise offer a refresh rather than pulling rows out from
  // under a part-built match.
  const syncSeen = React.useRef(lastMatchableSync);
  React.useEffect(() => {
    if (isR1 || lastMatchableSync === syncSeen.current) return;
    // Nothing seeds LastSync.matchableItems and the first queue poll is ~30s after page
    // load, so a modal opened inside that window starts with no baseline. That first
    // poll reports whenever the last sync actually ran - usually well before the page
    // was opened - and without this it would read as a sync that had just happened,
    // costing a duplicate fetch or, worse, a "this list changed" banner over a
    // selection that nothing had invalidated. Record the baseline and wait for a change.
    const hadBaseline = syncSeen.current !== undefined && syncSeen.current !== null;
    syncSeen.current = lastMatchableSync;
    if (!hadBaseline) return;
    // Clearing the flag matters when an earlier sync set it: the user may have set a
    // selection (marking the list stale), dropped it again, and then had a later sync
    // reload automatically. Without this the banner would keep claiming the list it
    // just reloaded is out of date, and keep offering a refresh that changes nothing.
    if (selected.length === 0) {
      setStaleSync(false);
      loadMatchables();
    } else {
      setStaleSync(true);
    }
  }, [lastMatchableSync])

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // const handleSelectAllClick = (event) => {
  //   if (event.target.checked) {
  //     setSelected(rows);
  //     return;
  //   }
  //   setSelected([]);
  // };
  const assignPayment = (arr, row, value) => arr.map(item => (item.txnId === row.txnId) ? { ...item, payment: value } : item);

  const getSelectedRow = row => selected && selected.find(e => e.txnId === row.txnId);

  const handleClick = (event, row) => {
    const isTextField = event.target.type === 'text'; // flag to determine if the mouse clicked on payment textfield.
    if (!isTextField) {
      const selectedRow = getSelectedRow(row);
      let newValue = 0;
      let newRows = [];
      let newSelected = [];
      if(selectedRow) {
        newSelected = [...(selected.filter(e => e.txnId !== row.txnId))];
        if(newSelected.length === 0) setFirstSelected({});
      } else {
        if (selected.length === 0) {
          setFirstSelected(row);
          if (row.type === 'SalesReceipt' || row.type === 'ReceivePayment' || row.type === 'JournalEntry') {
            newValue = row.balanceRemaining;
          } else {
            newValue = invoiceTotal <= 0 ? 0 : Math.min(invoiceTotal, row.balanceRemaining);
          }
        } else {
          if (isUndepositedSelected) {
            newValue = row.balanceRemaining;
          } else {
            newValue = invoiceTotal <= 0 ? 0 : Math.min(invoiceTotal, row.balanceRemaining);
          }
        }
        newSelected = [...selected, row];
        newSelected = assignPayment(newSelected, row, newValue); 
      }
      newRows = assignPayment(rows, row, newValue);
      setRows(newRows);
      setSelected(newSelected);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function ccyFormat(num) {
    // const minusSign = num < 0 ? '-' : '';
    // return num ? `${minusSign} $ ${Math.abs(num).toFixed(2)}` : 0;
    return num ? `$ ${Math.abs(num).toFixed(2)}` : 0;
  }
  function subtotal(items) {
    return items.map(({ payment }) => payment * 1).reduce((sum, i) => sum + i, 0);
  }

  const isDisabled = row => {
    if(firstSelected.type) {
      if(firstSelected.type === 'Invoice' || firstSelected.type === 'Bill') {
        if (firstSelected.contactId && (firstSelected.contactId !== row.contactId)) return true;
        if (firstSelected.type !== row.type) return true;
      } else {
        if(row.type === 'Invoice') return true;
      }
    }
    return false;
  }
  
  const formatType = type => type.replace(/([A-Z])/g, ' $1').trim();

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <div className={[cssClasses.selectDiv, selected.length && cssClasses.highlight].join(' ')}>{selected.length > 0 ? `${selected.length} Selected` : 'Select transaction(s) to match'}</div>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={rowsPerPage > 5 ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              // onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {/* Rows win whenever there are any: a list that has already arrived is
                  shown even while a slower one is still in flight, and the spinner is
                  only used when there is genuinely nothing to display yet. */}
              {rows.length === 0 ?
              <TableRow style={{ height: (rowsPerPage > 5 ? (33 * 10) : (53 * 5))  }}>
                <TableCell colSpan={colSpan} style={{ textAlign: 'center' }}>
                  { loading ? <Spinner small={true} />
                    : listErrors.length > 0 ?
                      <div>
                        <div className={cssClasses.matchWarning}>{listErrors.join('. ')}.</div>
                        <button type="button" className={[cssClasses.btn, cssClasses.btnSecondary].join(' ')} onClick={refresh}>Try again</button>
                      </div>
                    : 'No match found' }
                </TableCell>
              </TableRow>
              : stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const rowSelected = getSelectedRow(row);
                  const isRowSelected = !!rowSelected;
                  if (isRowSelected) row.payment = rowSelected.payment;
                  const labelId = `enhanced-table-checkbox-${index}`;
                  const onChange = e => {
                    let newValue = e.target.value === '.' ? '0.' : e.target.value;
                    const isZero = /^[0]\d$/.test(newValue);
                    if (isZero) newValue = newValue[1];
                    const isAmount = /^([1-9][0-9]*(\.[0-9]{0,2})?|[0]?(\.[0-9]{0,2})?)$/.test(newValue);
                    if (newValue === '' || (isAmount && newValue <= row.balanceRemaining)) {
                      const newRows = assignPayment(rows, row, newValue)
                      const newSelected = assignPayment(selected, row, newValue)
                      setSelected(newSelected);                
                      setRows(newRows);
                    }
                  }
                
                  return (
                    <TableRow
                      classes={{ selected: classes.selected }}
                      className={[classes.tableRow, isDisabled(row) ? classes.disabled : ''].join(' ')}
                      hover
                      onClick={(event) => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isRowSelected}
                      tabIndex={-1}
                      key={row.txnId}
                      selected={isRowSelected}
                    >
                      <TableCell width="5%" padding="checkbox">
                        <Checkbox
                          checked={isRowSelected}
                          color="default"
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>
                      <TableCell id={labelId} scope="row" padding="none" width="10%">
                        {moment.utc(row.date).format('DD MMM YY')}
                      </TableCell>
                      <TableCell width="20%">{formatType(row.type)}</TableCell>
                      <TableCell width="5%">{row.reference}</TableCell>
                      <TableCell width="15%">{row.contact}</TableCell>
                      <TableCell width="15%" align="right">{`$ ${row.amount.toFixed(2)}`}</TableCell>
                      <TableCell width="15%" align="right">{`$ ${row.balanceRemaining.toFixed(2)}`}</TableCell>
                      <TableCell width="15%" align="right" padding={isRowSelected ? 'none' : 'default'}>
                        {isRowSelected ? 
                          <TextField 
                            style={{ marginRight: 10, padding: 1 }}
                            variant="outlined"
                            InputProps={{ 
                              inputProps: {
                                readOnly: isUndepositedSelected,
                                style: {
                                  fontSize: 14,
                                  textAlign: 'right',
                                  width: 65,
                                  backgroundColor: isUndepositedSelected ? '' : 'white',
                                  padding: 7
                                },
                              }
                            }}
                            disabled={isUndepositedSelected}
                            value={row.payment} 
                            onChange={onChange} 
                          /> : `$ ${row.payment.toFixed(2)}`}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {rows.length > 0 && rowsPerPage < 25 && emptyRows > 0 && (
                <TableRow style={{ height: (rowsPerPage > 5 ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={colSpan} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowOptions}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>

      {<div className={cssClasses.totalContainer}>
            {loading && rows.length > 0 && <div className={cssClasses.matchWarning}>
                  * Still loading — more items may appear.
            </div>}
            {!loading && rows.length > 0 && listErrors.length > 0 && <div className={cssClasses.matchWarning}>
                  * {listErrors.join('. ')}. Some items may be missing from this list.
                  &nbsp;<button type="button" className={[cssClasses.btn, cssClasses.btnSecondary].join(' ')} onClick={refresh}>Try again</button>
            </div>}
            {!loading && staleSync && <div className={cssClasses.matchWarning}>
                  * This list changed after a sync with your Reckon Accounts book.
                  &nbsp;<button type="button" className={[cssClasses.btn, cssClasses.btnSecondary].join(' ')} onClick={refresh}>Refresh</button>
            </div>}
            {!loading && syncErrorMessages.length > 0 && <div className={cssClasses.matchWarning}>
                  * {syncErrorMessages.join(' ')}
                  &nbsp;<button type="button" className={[cssClasses.btn, cssClasses.btnSecondary].join(' ')} onClick={refresh}>Reload list</button>
            </div>}
            {isUndepositedSelected && invoiceTotal !== 0 && <div className={cssClasses.matchWarning}>
                  * Total amount of undeposited items must be exactly the same as the amount of the transaction.
            </div>}
            {isInvoiceOrBillSelected && invoiceTotal < 0 && <div className={cssClasses.matchWarning}>
                  * Total amount of transactions must be less than or equal to the amount of the transaction.
            </div>}
            {hasZeroPayment && <div className={cssClasses.matchWarning}>
                  * Payment of a transaction must be greater than zero.
            </div>}
            <div className={cssClasses.flexEnd}>
                <div>Transaction Amount</div>
                <div className={cssClasses.amountDiv}>{ccyFormat(transactionAmount)}</div>
            </div>
            <div className={cssClasses.flexEnd}>
                <div>Selected Transactions</div>
                <div className={cssClasses.amountDiv}>{ccyFormat(invoiceSubtotal)}</div>
            </div>
            <div className={[cssClasses.flexEnd, cssClasses.totalDiv].join(' ')}>
                <div>Difference</div>
                <div className={cssClasses.amountDiv}>{ccyFormat(invoiceTotal)}</div>
            </div>
        </div>}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    matchables: state.matchables,
    lastMatchableSync: state.rah.LastSync ? state.rah.LastSync.matchableItems : undefined,
    queueState: state.rah.queueState
  };
}

export default connect(mapStateToProps, actions)(FindMatchTable);



// WEBPACK FOOTER //
// ./src/components/Transaction/FindMatchTable.js